import { createRouter, createWebHistory, RouteRecordRaw, NavigationGuard } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { STOCK_TRANSFER_ROLES, StockTransferRoute } from './stockTransferRoutes';

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { public: true },
  },
  {
    // CORA-155b: second-factor step after a password login returns a challenge.
    path: '/two-factor',
    name: 'TwoFactorChallenge',
    component: () => import('@/pages/auth/TwoFactorChallengePage.vue'),
    meta: { public: true },
  },
  {
    path: '/two-factor-recovery',
    name: 'TwoFactorRecovery',
    component: () => import('@/pages/auth/TwoFactorRecoveryPage.vue'),
    meta: { public: true },
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('@/pages/auth/ChangePasswordPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      // Common routes (accessible by all authenticated users)
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/pages/ProfilePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'legal/ai-disclaimer',
        name: 'AiDisclaimer',
        component: () => import('@/pages/legal/AiDisclaimerPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'components',
        name: 'ComponentsDemo',
        component: () => import('@/pages/ComponentsDemo.vue'),
        meta: { requiresAuth: true },
      },
      // Doctor routes
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/pages/doctor/DoctorDashboard.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        path: 'patients',
        name: 'Patients',
        component: () => import('@/pages/doctor/PatientsPage.vue'),
        meta: { requiresAuth: true, roles: ['doctor', 'nurse'] },
      },
      {
        path: 'appointments',
        name: 'Appointments',
        component: () => import('@/pages/doctor/AppointmentsPage.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        path: 'queue',
        name: 'Queue',
        component: () => import('@/pages/doctor/QueuePage.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        path: 'schedule',
        name: 'Schedule',
        component: () => import('@/pages/doctor/SchedulePage.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        path: 'availability',
        name: 'MyAvailability',
        component: () => import('@/pages/availability/DoctorAvailabilityPage.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        // CORA-161 — reviewer queue for patient prescription refill requests.
        path: 'refill-requests',
        name: 'RefillRequests',
        component: () => import('@/pages/doctor/RefillRequestsPage.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        // Canonical route — keyed by the encounter envelope, not the patient.
        // An encounter is the universal handle for any visit (walk-in or
        // scheduled), so the same URL works for active, past, and concurrent
        // visits without the page having to guess "which one?".
        path: 'consultation/encounter/:encounterId',
        name: 'DoctorConsultation',
        component: () => import('@/pages/doctor/DoctorConsultation.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      {
        // Backward compat — existing dashboard links use /consultation/<patientId>.
        // Resolves an encounter for the patient (see ConsultationRedirect.vue for
        // which one) and replaces the URL with the canonical
        // /consultation/encounter/:encounterId form, so deep links and history
        // entries keep working.
        path: 'consultation/:patientId',
        name: 'ConsultationByPatient',
        component: () => import('@/pages/doctor/ConsultationRedirect.vue'),
        meta: { requiresAuth: true, roles: ['doctor'] },
      },
      // Nurse routes
      {
        path: 'nurse-dashboard',
        name: 'NurseDashboard',
        component: () => import('@/pages/nurse/NurseDashboard.vue'),
        meta: { requiresAuth: true, roles: ['nurse'] },
      },
      {
        path: 'triage',
        name: 'TriageQueue',
        component: () => import('@/pages/nurse/NurseTriagePage.vue'),
        meta: { requiresAuth: true, roles: ['nurse'] },
      },
      {
        path: 'nurse-actions',
        name: 'NurseActions',
        component: () => import('@/pages/nurse/NurseActionsPage.vue'),
        meta: { requiresAuth: true, roles: ['nurse'] },
      },
      {
        path: 'nurse-schedule',
        name: 'NurseSchedule',
        component: () => import('@/pages/nurse/NurseSchedulePage.vue'),
        meta: { requiresAuth: true, roles: ['nurse'] },
      },
      // CORA-192 — real-time ER control board (severity-sorted queue + bed
      // availability). Same ER-facing roles the backend EmergencyController gates.
      {
        path: 'emergency',
        name: 'ErDashboard',
        component: () => import('@/pages/emergency/ErDashboard.vue'),
        meta: { requiresAuth: true, roles: ['nurse', 'receptionist', 'doctor'] },
      },
      {
        // CORA-293 — rapid-triage intake for walk-in/unregistered ER patients.
        path: 'emergency/intake',
        name: 'ErRapidTriageIntake',
        component: () => import('@/pages/emergency/RapidTriageIntakePage.vue'),
        meta: { requiresAuth: true, roles: ['nurse', 'receptionist', 'doctor'] },
      },
      // Reception routes
      {
        path: 'reception-dashboard',
        name: 'ReceptionDashboard',
        component: () => import('@/pages/reception/ReceptionDashboard.vue'),
        meta: { requiresAuth: true, roles: ['receptionist'] },
      },
      {
        // The retired check-in page's address, still in bookmarks; walk-ins now start from Patient Admission.
        path: 'check-in',
        redirect: '/check-in-patients',
      },
      {
        path: 'check-in-patients',
        name: 'CheckInPatients',
        component: () => import('@/pages/reception/CheckInPatients.vue'),
        meta: { requiresAuth: true, roles: ['receptionist'] },
      },
      {
        path: 'reception-appointments',
        name: 'ReceptionAppointments',
        component: () => import('@/pages/reception/AppointmentsPage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist'] },
      },
      {
        path: 'reception-patients',
        name: 'ReceptionPatients',
        component: () => import('@/pages/reception/PatientsPage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist', 'admin'] },
      },
      {
        // /pending-payments reuses the canonical invoice list (CORA-64) with the
        // outstanding-only variant — see InvoicesListPage `variant` prop.
        path: 'pending-payments',
        name: 'PendingPayments',
        component: () => import('@/pages/billing/InvoicesListPage.vue'),
        props: { variant: 'outstanding' },
        meta: { requiresAuth: true, roles: ['receptionist', 'billing'] },
      },
      {
        path: 'reception-queue',
        name: 'ReceptionQueue',
        component: () => import('@/pages/reception/QueuePage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist'] },
      },
      // CORA-64 — canonical billing list page used by reception / billing /
      // admin / hospital_admin to search & filter invoices across the hospital.
      {
        path: 'billing/invoices',
        name: 'BillingInvoices',
        component: () => import('@/pages/billing/InvoicesListPage.vue'),
        props: { variant: 'all' },
        meta: { requiresAuth: true, roles: ['receptionist', 'billing', 'admin', 'hospital_admin'] },
      },
      {
        // CORA-52 detail view — payment tabs, refund modal, receipt download.
        path: 'billing/invoices/:id',
        name: 'BillingInvoiceDetail',
        component: () => import('@/pages/billing/InvoiceDetailPage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist', 'billing', 'admin', 'hospital_admin'] },
      },
      {
        // CORA-52 entry point — assembles the invoice draft for the encounter
        // and routes to the editor / existing invoice depending on state.
        path: 'billing/checkout/:encounterId',
        name: 'BillingCheckout',
        component: () => import('@/pages/billing/EncounterCheckoutPage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist', 'billing', 'admin', 'hospital_admin'] },
      },
      // Doctor Management routes
      {
        path: 'doctors',
        name: 'DoctorList',
        component: () => import('@/pages/doctors/DoctorListPage.vue'),
        meta: { requiresAuth: true, roles: ['receptionist', 'admin'] },
      },
      {
        path: 'doctors/register',
        name: 'DoctorRegistration',
        component: () => import('@/pages/doctors/DoctorRegistration.vue'),
        meta: { requiresAuth: true, roles: ['receptionist', 'admin'] },
      },
      {
        path: 'doctors/:id/availability',
        name: 'DoctorAvailability',
        component: () => import('@/pages/availability/DoctorAvailabilityPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'super_admin', 'hospital_admin'] },
      },
      // Warehouse routes
      {
        path: 'warehouse',
        name: 'WarehouseDashboard',
        component: () => import('@/pages/warehouse/WarehouseDashboard.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      {
        path: 'warehouse/inventory',
        name: 'WarehouseInventory',
        component: () => import('@/pages/warehouse/InventoryListPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      {
        path: 'warehouse/warehouses',
        name: 'Warehouses',
        component: () => import('@/pages/warehouse/WarehousesPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      {
        path: 'warehouse/movements',
        name: 'WarehouseMovements',
        component: () => import('@/pages/warehouse/StockMovementsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      {
        path: 'warehouse/transfers',
        name: StockTransferRoute.List,
        component: () => import('@/pages/warehouse/StockTransfersPage.vue'),
        meta: { requiresAuth: true, roles: STOCK_TRANSFER_ROLES },
      },
      {
        path: 'warehouse/transfers/new',
        name: StockTransferRoute.New,
        component: () => import('@/pages/warehouse/StockTransferFormPage.vue'),
        meta: { requiresAuth: true, roles: STOCK_TRANSFER_ROLES },
      },
      {
        path: 'warehouse/transfers/:id',
        name: StockTransferRoute.Detail,
        component: () => import('@/pages/warehouse/StockTransferDetailPage.vue'),
        meta: { requiresAuth: true, roles: STOCK_TRANSFER_ROLES },
      },
      {
        path: 'warehouse/transfers/:id/edit',
        name: StockTransferRoute.Edit,
        component: () => import('@/pages/warehouse/StockTransferFormPage.vue'),
        meta: { requiresAuth: true, roles: STOCK_TRANSFER_ROLES },
      },
      {
        path: 'warehouse/alerts',
        name: 'WarehouseAlerts',
        component: () => import('@/pages/warehouse/InventoryAlertsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      {
        path: 'warehouse/reports',
        name: 'WarehouseReports',
        component: () => import('@/pages/warehouse/InventoryReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'stock_manager'] },
      },
      // Equipment routes (biomedical asset registry — CORA-241)
      {
        path: 'equipment',
        name: 'EquipmentList',
        component: () => import('@/pages/equipment/EquipmentListPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'biomedical'] },
      },
      {
        path: 'equipment/:id',
        name: 'EquipmentDetail',
        component: () => import('@/pages/equipment/EquipmentDetailPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin', 'biomedical'] },
      },
      // Admin routes
      {
        path: 'management-dashboard',
        name: 'ManagementDashboard',
        component: () => import('@/pages/admin/ManagementDashboard.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/reports',
        name: 'Reports',
        component: () => import('@/pages/admin/ReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/hemodialysis-patients',
        name: 'HemodialysisReportsList',
        component: () => import('@/pages/admin/hemodialysis-reports/HemodialysisReportsListPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/hemodialysis-patients/create',
        name: 'HemodialysisReportCreate',
        component: () => import('@/pages/admin/hemodialysis-reports/HemodialysisReportFormPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/hemodialysis-patients/:id/edit',
        name: 'HemodialysisReportEdit',
        component: () => import('@/pages/admin/hemodialysis-reports/HemodialysisReportFormPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        // Registered after `create` and `:id/edit` so the catch-all `:id` segment
        // does not shadow those more-specific sub-paths.
        path: 'reports/hemodialysis-patients/:id',
        name: 'HemodialysisReportDetail',
        component: () => import('@/pages/admin/hemodialysis-reports/HemodialysisReportDetailPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/hemodialysis-province-summary',
        name: 'HemodialysisProvinceSummary',
        component: () => import('@/pages/admin/hemodialysis-reports/HemodialysisProvinceSummaryPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/patients',
        name: 'PatientReports',
        component: () => import('@/pages/admin/reports/PatientReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/billing',
        name: 'BillingReports',
        component: () => import('@/pages/admin/reports/BillingReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/appointments',
        name: 'AppointmentReports',
        component: () => import('@/pages/admin/reports/AppointmentReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/exams',
        name: 'ExamReports',
        component: () => import('@/pages/admin/reports/ExamReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'reports/queue-performance',
        name: 'QueuePerformanceReports',
        component: () => import('@/pages/admin/reports/QueuePerformanceReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      // Super-admin organization/hospital management
      {
        path: 'admin/organizations',
        name: 'OrganizationsList',
        component: () => import('@/pages/admin/organizations/OrganizationsListPage.vue'),
        meta: { requiresAuth: true, roles: ['super_admin'] },
      },
      {
        path: 'admin/organizations/:id',
        name: 'OrganizationDetails',
        component: () => import('@/pages/admin/organizations/OrganizationDetailsPage.vue'),
        meta: { requiresAuth: true, roles: ['super_admin'] },
      },
      // Org-admin settings (own org + hospitals within own org)
      {
        path: 'settings/organization',
        name: 'OrganizationSettings',
        component: () => import('@/pages/settings/OrganizationSettingsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'settings/hospitals',
        name: 'HospitalsList',
        component: () => import('@/pages/settings/HospitalsListPage.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'settings/vat-rates',
        name: 'VatRatesSettings',
        component: () => import('@/pages/settings/VatRatesPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      {
        path: 'settings/hospitals/:id',
        name: 'HospitalDetails',
        component: () => import('@/pages/settings/HospitalDetailsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      // Admin management routes
      {
        // CORA-309 — audit-log viewer for the whole admin tier: super_admin,
        // org admin, and hospital_admin. Backend scopes rows to the caller's
        // tenant; the guard here only gates page access.
        path: 'admin/audit-logs',
        name: 'AuditLogs',
        component: () => import('@/pages/admin/AuditLogsPage.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'super_admin', 'hospital_admin'] },
      },
      {
        path: 'admin/users',
        name: 'UsersManagement',
        component: () => import('@/pages/admin/UsersManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/nurses',
        name: 'NursesManagement',
        component: () => import('@/pages/admin/NursesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/receptionists',
        name: 'ReceptionistsManagement',
        component: () => import('@/pages/admin/ReceptionistsManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/vaccines',
        name: 'VaccinesManagement',
        component: () => import('@/pages/admin/VaccinesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/treatments',
        name: 'TreatmentsManagement',
        component: () => import('@/pages/admin/TreatmentsManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/treatment-consumables',
        name: 'TreatmentConsumablesManagement',
        component: () => import('@/pages/admin/TreatmentConsumablesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      {
        path: 'admin/specialties',
        name: 'SpecialtiesManagement',
        component: () => import('@/pages/admin/SpecialtiesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/departments',
        name: 'DepartmentsManagement',
        component: () => import('@/pages/admin/DepartmentsManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'admin/medications',
        name: 'MedicationsManagement',
        component: () => import('@/pages/admin/MedicationsManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      {
        path: 'admin/medication-presentations',
        name: 'MedicationPresentationsManagement',
        component: () => import('@/pages/admin/MedicationPresentationsManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      {
        path: 'admin/exam-types',
        name: 'ExamTypesManagement',
        component: () => import('@/pages/admin/ExamTypesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'hospital_admin'] },
      },
      {
        path: 'admin/diagnoses',
        name: 'DiagnosesManagement',
        component: () => import('@/pages/admin/DiagnosesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'super_admin'] },
      },
      {
        path: 'admin/provinces',
        name: 'ProvincesManagement',
        component: () => import('@/pages/admin/ProvincesManagement.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'super_admin'] },
      },
      // Patient routes
      {
        path: 'patient-portal',
        name: 'PatientPortal',
        component: () => import('@/pages/patient/PatientPortal.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
      {
        path: 'patient-appointments',
        name: 'PatientAppointments',
        component: () => import('@/pages/patient/AppointmentsPage.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
      {
        path: 'medical-history',
        name: 'MedicalHistory',
        component: () => import('@/pages/patient/MedicalHistoryPage.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/pages/patient/InvoicesPage.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
      {
        path: 'home-vitals',
        name: 'HomeVitals',
        component: () => import('@/pages/patient/HomeVitalsPage.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
      {
        path: 'messages',
        name: 'PatientMessages',
        component: () => import('@/pages/patient/MessagesPage.vue'),
        meta: { requiresAuth: true, roles: ['patient'] },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// The landing route each role is sent to when it lacks access to the requested
// route, and the canonical target the bare `/dashboard` entry resolves to. Kept
// as a single source of truth so the role-denied redirect and the `/dashboard`
// redirect can never drift apart. `hr` gates no module yet (CORA-242) so it
// lands on the roles-free `/profile`, never the doctor-only `/dashboard` — that
// avoids the redirect loop a missing entry would cause.
export const ROLE_DASHBOARDS: Record<string, string> = {
  doctor: '/dashboard',
  nurse: '/nurse-dashboard',
  receptionist: '/reception-dashboard',
  reception: '/reception-dashboard',
  patient: '/patient-portal',
  admin: '/management-dashboard',
  billing: '/billing/invoices',
  stock_manager: '/warehouse',
  biomedical: '/equipment',
  hr: '/profile',
  super_admin: '/admin/organizations',
  hospital_admin: '/management-dashboard',
};

// Exported so it can be unit-tested in isolation (the same function object is
// registered on the router below). Driving the real router through lazy `.vue`
// page imports is not viable in jsdom, so the guard is tested directly with a
// mocked auth store and a spy `next`.
export const navigationGuard: NavigationGuard = (to, _from, next) => {
  const authStore = useAuthStore();

  // Public routes
  if (to.meta.public) {
    next();
    return;
  }

  // Check authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // Check role-based access
  if (to.meta.roles && authStore.user) {
    const hasRole = (to.meta.roles as string[]).includes(authStore.user.role);
    if (!hasRole) {
      // Redirect to role-specific dashboard
      const dashboardPath = ROLE_DASHBOARDS[authStore.user.role] || '/dashboard';
      next({ path: dashboardPath });
      return;
    }
  }

  // Redirect root path to role-specific dashboard
  if (to.path === '/dashboard' && authStore.user) {
    const dashboardPath = ROLE_DASHBOARDS[authStore.user.role];
    if (dashboardPath && dashboardPath !== to.path) {
      next({ path: dashboardPath });
      return;
    }
  }

  next();
};

router.beforeEach(navigationGuard);

export default router;
